import { AppDispatch } from "../store";
import { openPdfDeliveryDialog, PdfDeliveryChoice } from "../slices/pdfDeliveryDialogSlice";
import { printPdfBlob, downloadPdfBlob } from "../../utils/pdfDeliveryUtils";

/**
 * Module-level context: holds the blob and the promise resolve callback while
 * the chooser dialog is open. Blobs cannot be stored in Redux (not serialisable).
 */
let _pendingBlob: Blob | null = null;
let _pendingFileName = "";
let _pendingResolve: ((choice: PdfDeliveryChoice) => void) | null = null;

/**
 * Called by the PdfDeliveryDialog component when the user makes a choice.
 * Executes the delivery action and resolves the promise returned to the handler.
 */
export function resolvePdfDeliveryDialog(choice: PdfDeliveryChoice): void {
  const blob = _pendingBlob;
  const fileName = _pendingFileName;
  const resolve = _pendingResolve;

  // Clear pending state before acting so a second call is a no-op
  _pendingBlob = null;
  _pendingFileName = "";
  _pendingResolve = null;

  if (choice === "print" && blob) {
    printPdfBlob(blob);
  } else if (choice === "download" && blob) {
    downloadPdfBlob(blob, fileName);
  }

  resolve?.(choice);
}

/**
 * Show the PDF delivery chooser and return the user's choice.
 *
 * Usage in a thunk:
 *   const choice = await showPdfDeliveryDialog(dispatch, pdfBlob, fileName);
 *   // delivery (print / download) is handled inside the dialog resolve
 */
export function showPdfDeliveryDialog(
  dispatch: AppDispatch,
  blob: Blob,
  fileName: string,
  title?: string
): Promise<PdfDeliveryChoice> {
  return new Promise((resolve) => {
    _pendingBlob = blob;
    _pendingFileName = fileName;
    _pendingResolve = resolve;

    dispatch(openPdfDeliveryDialog({ fileName, title }));
  });
}
